import { Box, Button, CardContent, TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import OutlineCard from "../../../../../../components/OutlineCard";
import SuccessModal, {
  useSuccess,
} from "../../../../../../components/SuccessModal";
import Text from "../../../../../../components/Text";
import { PROVIDER_PATHS } from "../../../../../../constants/paths";
import useCurrentUser from "../../../../../../hooks/local-storage/use-current-user";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import { useEmail2FAOTP } from "../../../../../../queries/2fa/email-2FA-OTP";
import { useVerify2FA } from "../../../../../../queries/2fa/verify2FA";
import {
  DEFAULT_BG_COLOR,
  DEFAULT_KEY_LABEL_COLOR,
  DEFAULT_LABEL_COLOR,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../../utils/utils";

interface EmailOTPProps {
  redirectToStart?: () => void;
}

const EmailOTP = ({ redirectToStart }: EmailOTPProps) => {
  const navigate = useNavigate();

  const [openedSuccess, { open: openSuccess, close: closeSuccess }] =
    useSuccess(() => {
      navigate(PROVIDER_PATHS.DASHBOARD);
    });

  const [me] = useCurrentUser();

  const [email, _setEmail] = useState(me?.email ?? "");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const showSnackbar = useSnackbar();
  const { mutateAsync: sendEmail2FAOTP } = useEmail2FAOTP();
  const { mutateAsync: verify2FA } = useVerify2FA();

  const handleSendOTP = () => {
    const payload = {
      sendEmailOtpRequest: {
        email: email,
      },
      xTENANTID: getXtenantId() ?? undefined,
    };
    // console.log("Sending OTP to email:", email);
    showToastOnApiCall({
      apiCall: sendEmail2FAOTP(payload),
      showSnackbar,
      onSave: (res: any) => {
        // console.log("res sendEmail2FAOTP", res);
        if (res.data.code === "TWO_FACTOR_AUTH_EMAIL_OTP_SENT") {
          setIsOtpSent(true);
          showSnackbar({
            message: (res?.data?.message as any) || "OTP sent successfully",
            type: "success",
          });
        } else {
          showSnackbar({
            message: (res?.data?.message as any) || "Failed to send OTP",
            type: "error",
          });
        }
      },
    });
  };

  const handleVerifyOTP = () => {
    const otpString = otp.join("");
    // console.log("Verifying email OTP:", otpString);
    // Add verification logic here
    verify2FA({
      verify2FARequest: {
        code: otpString,
        email: email,
        login: false,
      },
      xTENANTID: getXtenantId() ?? undefined,
    })
      .then((res) => {
        if (res.data.code === "TWO_FACTOR_AUTH_EMAIL_OTP_INVALID") {
          showSnackbar({
            message: (res?.data?.message as any) || "Invalid code",
            type: "error",
          });
        } else if (res.data.code === "TWO_FACTOR_AUTH_EMAIL_OTP_VERIFIED") {
          // showSnackbar({ message: res?.data?.message as any || "Code verified successfully", type: "success" });
          openSuccess();
          otp.fill("");
        }
      })
      .catch((err) => {
        // console.log("err verify2FA", err);
        showSnackbar({
          message: (err?.data?.message as any) || "Failed to verify OTP",
          type: "error",
        });
      });
  };

  const handleBack = () => {
    // Go back to the main 2FA selection component
    redirectToStart?.();
    // navigate(PROVIDER_PATHS.ACCOUNT_SETTINGS);
  };

  const handleOtpChange = (index: number, value: string) => {
    // Only allow single digit and numbers
    if (value.length > 1) return;

    // Check if the value is a number (0-9)
    if (value && !/^[0-9]$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleResendCode = () => {
    // console.log("Resending OTP to email:", email);
    setResendTimer(60);
    // Add resend logic here
    handleSendOTP();
    otp.fill("");
  };

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
        padding: 2,
      }}
    >
      <SuccessModal
        label="Email OTP setup completed successfully."
        opened={openedSuccess}
        onClose={closeSuccess}
      />
      <OutlineCard
        sx={{
          width: "100%",
          maxWidth: 700,
          backgroundColor: DEFAULT_BG_COLOR,
        }}
      >
        <CardContent sx={{ padding: 5, textAlign: "center" }}>
          <Text
            size={16}
            sx={{
              color: DEFAULT_LABEL_COLOR,
              marginBottom: 2,
            }}
          >
            Set Up Email OTP Verification (Step 2 – Verify Your Email Address)
          </Text>

          <OutlineCard
            sx={{
              padding: 3,
              width: "100%",
            }}
          >
            {!isOtpSent ? (
              <>
                <Text
                  sx={{
                    color: DEFAULT_KEY_LABEL_COLOR,
                    marginBottom: 3,
                    textAlign: "left",
                  }}
                >
                  Your verification code will be sent to your registered email:{" "}
                  {email ? email.replace(/(.{1}).*@/, "$1*@") : "Loading..."}
                </Text>

                <Box
                  sx={{ display: "flex", gap: 2, justifyContent: "stretch" }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{
                      flex: 1,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSendOTP}
                    disabled={!email.trim()}
                    sx={{
                      flex: 1,
                    }}
                  >
                    Send Verification Code
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Text
                  sx={{
                    marginBottom: 3,
                    textAlign: "left",
                    color: DEFAULT_KEY_LABEL_COLOR,
                  }}
                >
                  We've sent a 6-digit code via email to{" "}
                  {email ? email.replace(/(.{1}).*@/, "$1*@") : "Loading..."}.
                  Enter it below to enable email OTP Verification.
                </Text>

                <Box sx={{ marginBottom: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      justifyContent: "center",
                      marginBottom: 2,
                    }}
                  >
                    {otp.map((digit, index) => (
                      <TextField
                        key={index}
                        inputRef={(el) => (inputRefs.current[index] = el)}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        inputProps={{
                          maxLength: 1,
                          style: {
                            textAlign: "center",
                            fontSize: 16,
                            fontWeight: 400,
                          },
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                        sx={{
                          width: 60,
                          "& .MuiOutlinedInput-root": {
                            height: 48,
                            "& fieldset": {
                              borderColor: "#B4B5B5",
                            },
                            "&:hover fieldset": {
                              borderColor: "#373D41",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#104E84",
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text
                      sx={{
                        color: DEFAULT_KEY_LABEL_COLOR,
                      }}
                    >
                      Verification code sent! Please check your email.
                    </Text>
                    <Button
                      variant="text"
                      onClick={handleResendCode}
                      disabled={resendTimer > 0}
                      sx={{
                        color: "primary.main",
                        fontWeight: 400,
                        "&:disabled": {
                          color: DEFAULT_KEY_LABEL_COLOR,
                        },
                      }}
                    >
                      {resendTimer > 0
                        ? `Resend Code (00:${resendTimer
                            .toString()
                            .padStart(2, "0")})`
                        : "Resend Code"}
                    </Button>
                  </Box>
                </Box>

                <Box
                  sx={{ display: "flex", gap: 2, justifyContent: "stretch" }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{
                      flex: 1,
                      color: DEFAULT_KEY_LABEL_COLOR,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleVerifyOTP}
                    disabled={otp.join("").length !== 6}
                    sx={{
                      flex: 1,
                      backgroundColor: "primary.main",
                    }}
                  >
                    Verify
                  </Button>
                </Box>
              </>
            )}
          </OutlineCard>

          <Text
            sx={{
              color: DEFAULT_KEY_LABEL_COLOR,
              marginTop: 2,
              textAlign: "center",
            }}
          >
            If this email is incorrect, Update Email before continuing.
          </Text>
        </CardContent>
      </OutlineCard>
    </Box>
  );
};

export default EmailOTP;
